<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { PrimitiveProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const badgeVariants = cva(
  [
    'inline-flex items-center shrink-0 rounded-full font-normal tracking-tighter font-mono whitespace-nowrap',
    'border',
    '[&_svg]:shrink-0 [&_svg]:pointer-events-none [&_svg]:size-3',
    '[&_[data-slot=avatar]]:size-4 [&_[data-slot=avatar]]:shrink-0 [&_[data-slot=avatar-fallback]]:text-2xs',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-primary-300/10 text-primary-300 border-primary-300/30',
        gray: 'bg-gray-600/10 text-secondary border-gray-600/30',
        neutral: 'bg-neutral-600/30 text-neutral-800 border-neutral-600/60',
        green: 'bg-green-600/10 text-green-600 border-green-600/30',
        yellow: 'bg-yellow-700/10 text-yellow-800 border-yellow-700/30',
        red: 'bg-error/10 text-red-700 border-error/30',
        accent: 'bg-accent/10 text-accent border-accent/30',
        'accent-solid': 'bg-accent text-primal-inverted border-accent/30',
      },
      size: {
        sm: 'px-2 py-1 text-mono-sm leading-none gap-1',
        md: 'px-3 py-2 text-mono-md leading-none gap-2',
        lg: 'px-4 py-3 text-mono-lg leading-none gap-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
    },
  },
)

export type BadgeVariants = VariantProps<typeof badgeVariants>

export interface BadgeProps extends PrimitiveProps {
  variant?: BadgeVariants['variant']
  size?: BadgeVariants['size']
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<BadgeProps>(), {
  as: 'span',
  variant: 'primary',
  size: 'sm',
})
</script>

<template>
  <Primitive
    data-slot="badge"
    :as="props.as"
    :as-child="props.asChild"
    :data-variant="props.variant"
    :class="cn(badgeVariants({ variant: props.variant, size: props.size }), props.class)"
  >
    <slot />
  </Primitive>
</template>
